import { BACKEND_URL } from '@/lib/config';
import type {
  LegislationHistoryInterface,
  LegislationInterface,
  ReferencedDecisionsInterface,
} from '@/types/api-types';
import ApiService, { type ApiResponse } from './api-services';

class LegislationServices extends ApiService {
  constructor() {
    super(BACKEND_URL.LEGISLATION.BASE);
  }

  getLegislationRedirectIri(
    iri: string,
    asOf?: string | null
  ): ApiResponse<string | null> {
    // `asOf` carries the raw YYYYMMDD date segment extracted from a resolved
    // IRI (see extractEffectiveDateFromIri), but the backend's `as_of` is a
    // FastAPI/pydantic `date` query param, which requires dashed ISO
    // YYYY-MM-DD and rejects the compact, dash-free form with a 422 —
    // convert here at the API boundary. Malformed input (e.g. a hand-edited
    // URL) is dropped rather than forwarded, so it fails safe to the default
    // "latest" resolution.
    const isValidDateSegment = asOf && /^\d{8}$/.test(asOf);
    const asOfParam = isValidDateSegment
      ? `&as_of=${asOf.slice(0, 4)}-${asOf.slice(4, 6)}-${asOf.slice(6, 8)}`
      : '';
    return this.getData(
      `${BACKEND_URL.LEGISLATION.REDIRECT_LEGISLATION_IRI}?iri=/${iri}${asOfParam}`
    );
  }

  getLegislationInfo(iri: string): ApiResponse<LegislationInterface> {
    return this.getData(
      `${BACKEND_URL.LEGISLATION.GET_LEGISLATION_INFO}?iri=/${iri}`
    );
  }

  getLegislationHistory(
    iri: string
  ): ApiResponse<LegislationInterface | LegislationHistoryInterface[]> {
    return this.getData(`${BACKEND_URL.LEGISLATION.HISTORY}?iri=${iri}`);
  }

  getLegislationReferencedDecisions(
    refIdWithId: string,
    page = 1,
    size = 10
  ): ApiResponse<ReferencedDecisionsInterface[]> {
    const options = {
      refId: refIdWithId,
      page: page,
      size: size,
    };

    return this.postData(BACKEND_URL.LEGISLATION.REFERENCED_DECISIONS, options);
  }

  getLegislationReferencedDecisionsCount(
    refIdWithId: string
  ): ApiResponse<number> {
    const options = {
      refId: refIdWithId,
    };

    return this.postData(
      BACKEND_URL.LEGISLATION.REFERENCED_DECISIONS_COUNT,
      options
    );
  }
}

export default new LegislationServices();
